import { z } from 'zod';

// ========== Base Page Element Schema ==========
const BasePageElementSchema = z.object({
  id: z.string(),
  type: z.string(),
  label: z.string().optional(),
  position: z.object({ x: z.number(), y: z.number() }).optional(),
  required: z.boolean().default(false),
  varId: z.string().optional(), // GUID of the variable this element writes to
});

// ========== Input Question Elements ==========
const ShortTextElementSchema = BasePageElementSchema.extend({
  type: z.literal('ShortText'),
  placeholder: z.string().optional(),
  defaultValue: z.string().optional(),
  maxLength: z.number().optional(),
  validation: z.enum(['none', 'email', 'phone', 'url']).default('none'),
});

const MultilineElementSchema = BasePageElementSchema.extend({
  type: z.literal('Multiline'),
  placeholder: z.string().optional(),
  defaultValue: z.string().optional(),
  rows: z.number().default(4),
  maxLength: z.number().optional(),
});

const DateElementSchema = BasePageElementSchema.extend({
  type: z.literal('Date'),
  dateFormat: z.enum(['MM/DD/YYYY', 'DD/MM/YYYY', 'YYYY-MM-DD']).default('MM/DD/YYYY'),
  minDate: z.string().optional(),
  maxDate: z.string().optional(),
});

const NumberElementSchema = BasePageElementSchema.extend({
  type: z.literal('Number'),
  min: z.number().optional(),
  max: z.number().optional(),
  step: z.number().optional(),
  defaultValue: z.number().optional(),
});

const PasswordElementSchema = BasePageElementSchema.extend({
  type: z.literal('Password'),
  minLength: z.number().optional(),
  requireSpecialChar: z.boolean().default(false),
  requireNumber: z.boolean().default(false),
});

const ImageUploadElementSchema = BasePageElementSchema.extend({
  type: z.literal('ImageUpload'),
  // Matches the platform ceiling in apps/api/src/assets/image-validator.ts.
  // The server intersects the two, so a lower value here tightens; a higher
  // one has no effect.
  maxSizeMB: z.number().default(25),
  acceptedFormats: z.array(z.string()).default(['jpg', 'png', 'webp', 'gif']),
  multiple: z.boolean().default(false),
});

// ========== Button Elements ==========
const ButtonElementSchema = BasePageElementSchema.extend({
  type: z.literal('Button'),
  buttonText: z.string(),
  buttonStyle: z.enum(['primary', 'secondary', 'outline']).default('primary'),
  targetPageId: z.string().optional(), // ID of the node/page to navigate to
  action: z.enum(['navigate', 'submit', 'custom']).default('navigate'),
});

// ========== Selection Elements ==========
const YesNoElementSchema = BasePageElementSchema.extend({
  type: z.literal('YesNo'),
  yesLabel: z.string().default('Yes'),
  noLabel: z.string().default('No'),
  defaultValue: z.boolean().optional(),
  createBranches: z.boolean().default(false), // If true, creates branches to other pages
  branches: z.object({
    yes: z.string().optional(), // Target page ID for "yes"
    no: z.string().optional(),  // Target page ID for "no"
  }).optional(),
});

const MultiChoiceElementSchema = BasePageElementSchema.extend({
  type: z.literal('MultiChoice'),
  options: z.array(z.string()),
  allowMultiple: z.boolean().default(false),
  displayStyle: z.enum(['radio', 'checkbox', 'dropdown']).default('radio'),
  createBranches: z.boolean().default(false), // If true, creates branches to other pages
  branches: z.array(z.object({
    option: z.string(), // The option text
    // Optional because an option that has not been pointed at a page yet is a
    // valid intermediate state: the editor draws it as a dangling stub to
    // connect later, and the runtime treats a missing target as "no branch".
    // Requiring it here rejected flows the editor can legitimately produce.
    targetPageId: z.string().optional(), // Target page ID for this option
  })).optional(),
});

const RatingElementSchema = BasePageElementSchema.extend({
  type: z.literal('Rating'),
  maxRating: z.number().default(5),
  ratingStyle: z.enum(['stars', 'numbers', 'emoji']).default('stars'),
  labels: z.object({
    low: z.string().optional(),
    high: z.string().optional(),
  }).optional(),
});

// ========== Visual Elements ==========
const RichTextElementSchema = BasePageElementSchema.extend({
  type: z.literal('RichText'),
  content: z.string(), // HTML content
});

const CollectionDisplayElementSchema = BasePageElementSchema.extend({
  type: z.literal('CollectionDisplay'),
  dataSource: z.string(), // Variable path or API endpoint
  itemTemplate: z.string(), // Template for each item
  displayStyle: z.enum(['grid', 'list', 'carousel']).default('list'),
  itemsPerPage: z.number().default(10),
});

const TableDisplayElementSchema = BasePageElementSchema.extend({
  type: z.literal('TableDisplay'),
  dataSource: z.string(), // Variable path or API endpoint
  columns: z.array(z.object({
    key: z.string(),
    label: z.string(),
    width: z.string().optional(),
    sortable: z.boolean().default(false),
  })),
  pagination: z.boolean().default(true),
  rowsPerPage: z.number().default(10),
});

// ========== Discriminated Union of All Page Elements ==========
export const PageElementSchema = z.discriminatedUnion('type', [
  // Input Questions
  ShortTextElementSchema,
  MultilineElementSchema,
  DateElementSchema,
  NumberElementSchema,
  PasswordElementSchema,
  ImageUploadElementSchema,
  // Buttons
  ButtonElementSchema,
  // Selections
  YesNoElementSchema,
  MultiChoiceElementSchema,
  RatingElementSchema,
  // Visuals
  RichTextElementSchema,
  CollectionDisplayElementSchema,
  TableDisplayElementSchema,
]);

// ========== Type Exports ==========
export type PageElement = z.infer<typeof PageElementSchema>;
export type ShortTextElement = z.infer<typeof ShortTextElementSchema>;
export type MultilineElement = z.infer<typeof MultilineElementSchema>;
export type DateElement = z.infer<typeof DateElementSchema>;
export type NumberElement = z.infer<typeof NumberElementSchema>;
export type PasswordElement = z.infer<typeof PasswordElementSchema>;
export type ImageUploadElement = z.infer<typeof ImageUploadElementSchema>;
export type ButtonElement = z.infer<typeof ButtonElementSchema>;
export type YesNoElement = z.infer<typeof YesNoElementSchema>;
export type MultiChoiceElement = z.infer<typeof MultiChoiceElementSchema>;
export type RatingElement = z.infer<typeof RatingElementSchema>;
export type RichTextElement = z.infer<typeof RichTextElementSchema>;
export type CollectionDisplayElement = z.infer<typeof CollectionDisplayElementSchema>;
export type TableDisplayElement = z.infer<typeof TableDisplayElementSchema>;
